import { Box, Chip, Grid, IconButton, Typography } from "@mui/material";
import { useState } from "react";

import { trackEvent } from "utils/tracking";

import StepperFooter from "./SharedComponents/StepperFooter";

type CampaignMethod = "manual" | "ai";

interface Props {
  onClose: () => void;
  onManual: () => void;
  onUseAI: () => void;
}

interface MethodCardProps {
  icon: string;
  title: string;
  subtitle: string;
  badge: { label: string; recommended: boolean };
  selected: boolean;
  onSelect: () => void;
}

function MethodCard({
  icon,
  title,
  subtitle,
  badge,
  selected,
  onSelect,
}: MethodCardProps) {
  return (
    <Box
      onClick={onSelect}
      sx={{
        position: "relative",
        height: "100%",
        minHeight: 168,
        p: 2.5,
        borderRadius: 3,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        border: selected ? "2px solid" : "1px solid",
        borderColor: selected ? "primary.main" : "rgba(1,67,57,0.12)",
        backgroundColor: selected ? "#fafaf6" : "#fff",
        transition: "all 0.15s ease",
        "&:hover": {
          borderColor: "primary.main",
          transform: "translateY(-2px)",
          boxShadow: "0 6px 18px rgba(1,67,57,0.1)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: selected ? "primary.main" : "rgba(1,67,57,0.06)",
          }}
        >
          <Box
            component="i"
            className={icon}
            sx={{
              fontSize: 18,
              color: selected ? "#fff" : "primary.main",
            }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={badge.label}
            size="small"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              height: 22,
              ...(badge.recommended
                ? {
                    bgcolor: "#dcfce7",
                    color: "#008236",
                    border: "1px solid #a7f3d0",
                  }
                : {
                    bgcolor: "#fff7ed",
                    color: "#ca3500",
                    border: "1px solid #ffd6a8",
                  }),
              "& .MuiChip-label": { px: 1 },
            }}
          />
          <Box
            component="i"
            className={
              selected ? "fa-solid fa-circle-check" : "fa-regular fa-circle"
            }
            sx={{
              fontSize: 18,
              color: selected ? "primary.main" : "rgba(1,67,57,0.3)",
            }}
          />
        </Box>
      </Box>
      <Box>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0F2F2A" }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: "#4A5565", mt: 0.5 }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}

export default function CampaignMethodChoice({
  onClose,
  onManual,
  onUseAI,
}: Props) {
  const [method, setMethod] = useState<CampaignMethod>("manual");

  const handleNext = () => {
    trackEvent("New Campaign Method Selected", {
      Method: method === "ai" ? "AI" : "Manual",
    });
    if (method === "ai") onUseAI();
    else onManual();
  };

  return (
    <>
      <Box sx={{ px: { xs: 2.5, md: 4 }, pt: { xs: 3, md: 4 } }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "text.secondary",
            width: 25,
            height: 25,
          }}
        >
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={{ fontSize: 20 }}
          />
        </IconButton>

        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: { xs: 20, md: 24 },
              fontWeight: 700,
              lineHeight: 1.3,
            }}
          >
            Create New Campaign
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#4A5565", mt: 0.5 }}>
            How would you like to build this campaign?
          </Typography>
        </Box>

        <Grid
          container
          spacing={2}
          justifyContent="center"
          sx={{ maxWidth: 620, mx: "auto" }}
        >
          <Grid item xs={12} sm={6}>
            <MethodCard
              icon="fa-solid fa-sliders"
              title="Set it up myself"
              subtitle="Pick your brands, templates, and schedule step by step."
              badge={{ label: "Recommended", recommended: true }}
              selected={method === "manual"}
              onSelect={() => setMethod("manual")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MethodCard
              icon="fa-solid fa-sparkles"
              title="Build with AI"
              subtitle="Answer a few questions and let the assistant pick brands and templates for you."
              badge={{ label: "Beta", recommended: false }}
              selected={method === "ai"}
              onSelect={() => setMethod("ai")}
            />
          </Grid>
        </Grid>
      </Box>

      <StepperFooter handleBack={onClose} handleNext={handleNext} />
    </>
  );
}
